import React from 'react';
import { ColorResult, CompactPicker, GithubPicker, SketchPicker } from 'react-color';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';

import { IColorPickerConfiguration, IColorPickerProps, IColorPickerState } from './models/index.js';
import ActiveColorIndicator from './styledComponents/ActiveColorIndicator.js';
import ClickLayer from './styledComponents/ClickLayer.js';
import ColorIndicator from './styledComponents/ColorIndicator.js';
import PickerWrapper from './styledComponents/PickerWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { COLOR_PICKER_CATEGORY_KEY, COLOR_PICKER_COMPONENT_KEY } from './ColorPicker.meta.js';

export class ColorPicker extends ComponentBase<IColorPickerProps, IColorPickerState, IColorPickerConfiguration> {
	categoryKey = COLOR_PICKER_CATEGORY_KEY;
	componentKey = COLOR_PICKER_COMPONENT_KEY;

	constructor(props: IColorPickerProps) {
		super(props);

		makeObservable(this, {
			color: observable,
			showPicker: observable,
			togglePicker: action,
			handleColorIndicatorClick: action,
			handleColorChange: action,
		});

		this.color = props.value || '#000000';
	}

	color: string;

	showPicker = false;

	togglePicker = () => {
		const { disabled } = this.props;
		if (!disabled) {
			this.showPicker = !this.showPicker;
		}
	};

	handleColorIndicatorClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		this.togglePicker();
	};

	handleColorChange = (color: ColorResult) => {
		const { onChange } = this.props;
		this.color = color.hex;
		if (onChange) {
			onChange(this.color);
		}
	};

	render() {
		const { className, disabled, type = 'sketch', colors } = this.props;
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const Color = disabled ? ColorIndicator : ActiveColorIndicator;
		return (
			<Wrapper className={className}>
				<Color style={{ backgroundColor: this.color }} onClick={this.handleColorIndicatorClick} />
				{this.showPicker && (
					<React.Fragment>
						<ClickLayer onClick={this.togglePicker} />
						<PickerWrapper>
							{type === 'compact' && (
								<CompactPicker
									colors={colors}
									color={this.color}
									onChangeComplete={this.handleColorChange}
								/>
							)}
							{type === 'github' && (
								<GithubPicker
									colors={colors}
									color={this.color}
									onChangeComplete={this.handleColorChange}
								/>
							)}
							{type === 'sketch' && (
								<SketchPicker
									presetColors={colors}
									color={this.color}
									onChangeComplete={this.handleColorChange}
								/>
							)}
						</PickerWrapper>
					</React.Fragment>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(ColorPicker), COLOR_PICKER_CATEGORY_KEY, COLOR_PICKER_COMPONENT_KEY);
